/* global globalThis */
// Bounded memory only. Keys scope DOM observations, never logs or business IDs.
const traces = new WeakSet();
const pending = new Map();
const views = new Map();
const LIMIT = 32;
function boundedSet(map, key, value) {
  map.delete(key); map.set(key, value);
  while (map.size > LIMIT) map.delete(map.keys().next().value);
}
function scopeKey(storeId, phone) {
  return Number(storeId) > 0 && phone ? JSON.stringify([Number(storeId), String(phone)]) : null;
}
function randomId() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
}

export function createConversationDiagnostic(storeId, {
  clock = () => performance.now(), log = line => console.log(line), idFactory = randomId,
} = {}) {
  try {
    const traceId = idFactory();
    if (!/^[a-f0-9]{32}$/.test(traceId || "")) return null;
    const started = clock();
    let events = 0;
    const trace = {
      traceId, clock, key:null, finished:false, committed:false,
      expected:new Set(), merged:new Set(),
      mark(event, fields = {}, duration) {
        try {
          if (events >= 160 || !/^[a-z_]{1,80}$/.test(event)) return;
          events += 1;
          const safe = {};
          for (const field of ["status", "records"]) {
            if (Number.isFinite(fields[field]) && fields[field] >= 0) safe[field] = fields[field];
          }
          log(`[conversation-diagnostic] ${JSON.stringify({
            side:"browser", traceId, storeId:Number(storeId) > 0 ? Number(storeId) : null, event,
            elapsedMs:Math.round(Math.max(0, clock() - started) * 100) / 100,
            ...(Number.isFinite(duration) ? { durationMs:Math.round(Math.max(0, duration) * 100) / 100 } : {}),
            ...safe,
          })}`);
        } catch { /* Observation never escapes. */ }
      },
    };
    traces.add(trace);
    return trace;
  } catch { return null; }
}
export function conversationMark(trace, event, fields) {
  try { if (traces.has(trace)) trace.mark(event, fields); } catch { /* Fail open. */ }
}
export function conversationPhase(trace, phase) {
  try {
    if (!traces.has(trace)) return () => {};
    const started = trace.clock();
    let ended = false;
    conversationMark(trace, `${phase}_start`);
    return () => {
      try {
        if (ended) return;
        ended = true;
        trace.mark(`${phase}_end`, {}, trace.clock() - started);
      } catch { /* Fail open. */ }
    };
  } catch { return () => {}; }
}
export function conversationHeaders(trace) {
  try { return traces.has(trace) ? { "X-CRM-Conversation-Trace":trace.traceId } : {}; } catch { return {}; }
}
export function noteConversationSelection(storeId, phone) {
  try {
    const key = scopeKey(storeId, phone);
    if (!key) return null;
    let trace = pending.get(key);
    if (!trace || trace.finished) {
      trace = createConversationDiagnostic(storeId);
      if (!trace) return null;
      trace.key = key;
      boundedSet(pending, key, trace);
    }
    conversationMark(trace, "selection");
    return trace;
  } catch { return null; }
}
export function loadConversationDiagnostic(storeId, phone) {
  try {
    const key = scopeKey(storeId, phone);
    const trace = pending.get(key);
    const current = trace && !trace.finished ? trace : noteConversationSelection(storeId, phone);
    conversationMark(current, "load_history_start");
    return current;
  } catch { return null; }
}
export function prepareConversationMerges(trace, labels) {
  try {
    if (!traces.has(trace)) return;
    trace.expected = new Set(labels);
    trace.merged.clear();
    trace.committed = false;
  } catch { /* No state changes. */ }
}
export function observeConversationMerge(trace, phase, operation) {
  const end = conversationPhase(trace, phase);
  try {
    const result = operation();
    try { if (traces.has(trace)) trace.merged.add(phase); } catch { /* No state changes. */ }
    return result;
  } finally { end(); }
}
export function finishConversationDiagnostic(trace) {
  try {
    if (!traces.has(trace)) return;
    trace.finished = true;
    conversationMark(trace, "load_history_end");
  } catch { /* Fail open. */ }
}
export function registerConversationView(storeId, phone, node) {
  try {
    const key = scopeKey(storeId, phone);
    if (!key || !node) return () => {};
    boundedSet(views, key, node);
    const trace = pending.get(key);
    if (trace?.expected.size && !trace.committed && [...trace.expected].every(label => trace.merged.has(label))) {
      trace.committed = true;
      conversationMark(trace, "state_commit");
    }
    return () => { try { if (views.get(key) === node) views.delete(key); } catch { /* No state changes. */ } };
  } catch { return () => {}; }
}
export function observeConversationFrame(trace, {
  frame = callback => requestAnimationFrame(callback),
  find = () => views.get(trace.key),
  visible = node => {
    const rect = node.getBoundingClientRect();
    const style = getComputedStyle(node);
    return node.isConnected && rect.width > 0 && rect.height > 0
      && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth
      && style.display !== "none" && style.visibility !== "hidden";
  }, maxFrames = 120,
} = {}) {
  try {
    if (!traces.has(trace)) return;
    let count = 0;
    const check = () => {
      try {
        if (trace.key && pending.get(trace.key) !== trace && !trace.committed) {
          conversationMark(trace, "conversation_frame_superseded");
          return;
        }
        const node = find();
        if (trace.committed && node && visible(node)) {
          conversationMark(trace, "conversation_visible_frame");
        } else if (++count < maxFrames) {
          frame(check);
        } else {
          conversationMark(trace, "conversation_frame_not_observed");
        }
      } catch { /* Rendering unaffected. */ }
    };
    frame(check);
  } catch { /* No RAF in background/tests. */ }
}